import { useEffect, useState } from "react"
import { router } from "@inertiajs/react"
import { useTranslation } from "react-i18next"
import { Globe } from "lucide-react"

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"

const SUPPORTED_LANGUAGES = [
  "es",
  "en",
  "zh-CN",
  "pt-BR",
  "it",
  "fr",
  "ja",
  "ko",
  "am",
]
const LS_KEY = "i18nextLng"

export const LANGUAGE_NAMES: Record<string, string> = {
  am: "አማርኛ (Amharic)",
  en: "English",
  es: "Español",
  fr: "Français",
  it: "Italiano",
  ja: "日本語 (Japanese)",
  ko: "한국어 (Korean)",
  "pt-BR": "Português (Brasil)",
  "zh-CN": "中文 (简体)",
}

interface LanguageDetectionModalProps {
  userLanguage?: string | null
  guestMode?: boolean
}

export function LanguageDetectionModal({
  userLanguage = null,
  guestMode = false,
}: LanguageDetectionModalProps) {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const [detectedLanguage, setDetectedLanguage] = useState<string | null>(null)

  useEffect(() => {
    if (!guestMode) {
      // Authenticated mode: apply user's saved language and skip modal
      if (userLanguage) {
        i18n.changeLanguage(userLanguage)
        return
      }
      // If user dismissed the modal previously (stored in localStorage), don't re-show
      const stored = localStorage.getItem(LS_KEY)
      if (stored && SUPPORTED_LANGUAGES.includes(stored)) {
        return
      }
      // No saved preference anywhere — fall through to detection
    } else {
      // Guest mode: if localStorage already has a preference, apply it silently
      const stored = localStorage.getItem(LS_KEY)
      if (stored && SUPPORTED_LANGUAGES.includes(stored)) {
        i18n.changeLanguage(stored)
        return
      }
    }

    // Detect browser language
    const fullLang = navigator.language // e.g., 'pt-BR', 'zh-CN'
    const shortLang = navigator.language.split("-")[0] // e.g., 'es', 'pt', 'zh'

    let detected: string | null = null
    if (SUPPORTED_LANGUAGES.includes(fullLang)) {
      detected = fullLang
    } else if (SUPPORTED_LANGUAGES.includes(shortLang)) {
      detected = shortLang
    }

    if (detected) {
      setDetectedLanguage(detected)
      setOpen(true)
    }
  }, [userLanguage, guestMode, i18n])

  const getTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone

  const handleAccept = async () => {
    if (!detectedLanguage) return

    i18n.changeLanguage(detectedLanguage)
    // i18next LanguageDetector writes to localStorage automatically via caches config

    if (!guestMode) {
      // Authenticated: persist to backend and reload
      const csrfToken = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content")
      try {
        await fetch("/api/update_language", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken || "",
          },
          body: JSON.stringify({ language: detectedLanguage }),
        })
        router.reload()
      } catch (error) {
        console.error("Failed to update language:", error)
      }
    }

    setOpen(false)
  }

  const handleDismiss = () => {
    // Save current language so modal doesn't re-appear
    localStorage.setItem(LS_KEY, i18n.language)
    setOpen(false)
  }

  if (!detectedLanguage) return null

  const langName = LANGUAGE_NAMES[detectedLanguage] || detectedLanguage

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleDismiss()
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mb-2 flex items-center gap-3">
            <div className="bg-primary/10 flex h-12 w-12 items-center justify-center rounded-full">
              <Globe className="text-primary h-6 w-6" />
            </div>
            <div>
              <DialogTitle>{t("language_modal.title")}</DialogTitle>
            </div>
          </div>
          <DialogDescription className="text-base">
            {t("language_modal.description", {
              language: langName,
              timezone: getTimeZone(),
            })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button
            variant="outline"
            onClick={handleDismiss}
            className="w-full sm:w-auto"
          >
            {t("language_modal.dismiss")}
          </Button>
          <Button onClick={handleAccept} className="w-full sm:w-auto">
            {t("language_modal.accept", { language: langName })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
